/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * The saga for load Table sub rows
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.1.0
 */
import moment from 'moment';
import type { SagaIterator } from 'redux-saga';
import { call, debounce, put, select } from 'redux-saga/effects';

import { requestSaga } from '@/main/sagas/requestSaga';
import { API_HOSTNAME } from '@modules/app/Api';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';
import { makeSelectorExpensesAnalyzerType } from '@pages/PageExpensesAnalyzer/pageExpensesAnalyzerSelectors';
import { MapExpensesAnalyzerTypeToMeta } from '@pages/PageExpensesAnalyzer/types/pageExpensesAnalyzerMapTypes';
import { EExpensesAnalyzerTypes } from '@pages/PageExpensesAnalyzer/types/pageExpensesAnalyzerTypes';

import {
  addTableSubRowId,
  collapseTableSubRows,
  loadTableSubRowError,
  setTableSubRowData,
  setTableSubRowMessage,
} from '../actions/tableActions';
import { LOAD_TABLE_SUB_ROW } from '../constants/actionConstants';
import {
  makeSelectorTableCurrentName,
  makeSelectorTableData,
  makeSelectorTableFilter,
} from '../selectors/tableSelectors';
import { MapTableNameToDetailsApiPath } from '../types/tableMapTypes';
import type {
  TLoadTableSubRowAction,
  TSetTableSubRowMessageAction,
} from '../types/tableTypes';
import { TableNameType } from '../types/tableTypes';

export function* loadTableSubRowSaga(
  action: TLoadTableSubRowAction,
): SagaIterator {
  const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
  const apiPath = MapTableNameToDetailsApiPath[tableName];
  if (!apiPath) return;

  const { subRowId } = action.payload;
  yield put(addTableSubRowId({ subRowId }));

  yield put(collapseTableSubRows(false));
  const filters = yield select(makeSelectorTableFilter());
  const expensesAnalyzerType: EExpensesAnalyzerTypes = yield select(
    makeSelectorExpensesAnalyzerType(),
  );
  const { isBillsType } = MapExpensesAnalyzerTypeToMeta[expensesAnalyzerType];
  let queryString;

  try {
    if (tableName === TableNameType.expensesAnalyzer && isBillsType) {
      queryString = `id=${subRowId}`;
    } else if (filters.monthRange) {
      queryString = [
        `from=${filters.monthRange.valueFrom}`,
        `to=${filters.monthRange.valueTo}`,
        `id=${subRowId}`,
      ].join('&');
    } else {
      queryString = [
        `from=${moment()
          .startOf('month')
          .format(EFormatData.YearMonthReverse)}`,
        `to=${moment().endOf('month').format(EFormatData.YearMonthReverse)}`,
        `id=${subRowId}`,
      ].join('&');
    }
    const subRowsData = yield call(
      requestSaga,
      API_HOSTNAME + apiPath + '?' + queryString,
      'GET',
    );
    const tableData = yield select(makeSelectorTableData());
    const newData = tableData.map((item: any) => {
      const newItem = { ...item };
      if (newItem.id === subRowId) {
        newItem.subRows = subRowsData.map((item: any) => {
          if (item.month) {
            const startDate = moment(item.month)
              .startOf('month')
              .format(EFormatData.FullDataShortYear);
            const endDate = moment(item.month)
              .endOf('month')
              .format(EFormatData.FullDataShortYear);
            item.expandedColumn = `${startDate}-${endDate}`;
          }
          return item;
        });
      }
      return newItem;
    });
    yield put(setTableSubRowData({ data: newData }));
  } catch (error: any) {
    yield put(
      loadTableSubRowError({
        error,
        subRowId,
      }),
    );
    const translation = yield select(makeSelectTranslationCommon());
    yield put(
      setTableSubRowMessage({
        message: translation.commonTable.error,
        subRowId,
      }),
    );
  }
}

export function* setTableSubRowMessageSaga(
  action: TSetTableSubRowMessageAction,
): SagaIterator {
  const { message, subRowId } = action.payload;
  const tableData = yield select(makeSelectorTableData());
  const updatedTableData = tableData.map((item: any) => {
    if (item.id === subRowId)
      return { ...item, subRows: [{ expandedColumn: message }] };
    return item;
  });
  yield put(setTableSubRowData({ data: updatedTableData }));
}

export function* debounceLoadSubRows(): SagaIterator {
  // debounce by 500ms
  yield debounce(
    DEBOUNCE_DEFAULT_DELAY,
    LOAD_TABLE_SUB_ROW,
    loadTableSubRowSaga,
  );
}
